import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './ApisModule.css';

interface ExternalApiDefinition {
  id: number;
  name: string;
  sourceType: 'http' | 'database';
  endpointUrl: string;
  username: string;
  password: string;
  dbHost: string;
  dbPort: number;
  dbName: string;
  dbUser: string;
  dbPassword: string;
  queryTemplate: string;
  createNow: boolean;
  published: boolean;
  publicSlug: string;
  publicPath: string;
  lastConsumedAt: string | null;
  cacheInterval: number;
  cacheUpdatedAt: string | null;
  cacheStatus: 'pendente' | 'ok' | 'erro';
  cacheError: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiFormState {
  name: string;
  sourceType: 'http' | 'database';
  endpointUrl: string;
  username: string;
  password: string;
  dbHost: string;
  dbPort: number;
  dbName: string;
  dbUser: string;
  dbPassword: string;
  queryTemplate: string;
  cacheInterval: number;
  createNow: boolean;
  published: boolean;
}

const emptyForm: ApiFormState = {
  name: '',
  sourceType: 'http',
  endpointUrl: '',
  username: '',
  password: '',
  dbHost: '',
  dbPort: 3306,
  dbName: '',
  dbUser: '',
  dbPassword: '',
  queryTemplate: '',
  cacheInterval: 0,
  createNow: true,
  published: false
};

const ApisModule: React.FC = () => {
  const [apis, setApis] = useState<ExternalApiDefinition[]>([]);
  const [form, setForm] = useState<ApiFormState>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedApiId, setSelectedApiId] = useState<string>('');
  const [queryInput, setQueryInput] = useState('limit=10');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');

  const loadApis = async () => {
    setLoadingList(true);
    try {
      const response = await axios.get<ExternalApiDefinition[]>('/api/apis');
      setApis(response.data || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado';
      setRequestMessage(`Erro ao carregar APIs: ${message}`);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadApis();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowPassword(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'dbPort' || name === 'cacheInterval') {
      const parsedNum = Number(value);
      if (name === 'dbPort') {
        setForm(prev => ({ ...prev, dbPort: Number.isFinite(parsedNum) ? parsedNum : 3306 }));
      } else {
        setForm(prev => ({ ...prev, cacheInterval: Number.isFinite(parsedNum) && parsedNum >= 0 ? parsedNum : 0 }));
      }
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: ApiFormState = {
      ...form,
      name: form.name.trim(),
      endpointUrl: form.endpointUrl.trim(),
      username: form.username.trim(),
      password: form.password,
      dbHost: form.dbHost.trim(),
      dbName: form.dbName.trim(),
      dbUser: form.dbUser.trim(),
      dbPassword: form.dbPassword,
      queryTemplate: form.queryTemplate.trim()
    };

    if (!payload.name || !payload.username || !payload.password) {
      window.alert('Preencha nome, usuário e senha da API.');
      return;
    }

    if (payload.sourceType === 'http' && !payload.endpointUrl) {
      window.alert('Para fonte HTTP, preencha a URL de consumo.');
      return;
    }

    if (payload.sourceType === 'database' && (!payload.dbHost || !payload.dbName || !payload.dbUser || !payload.dbPassword)) {
      window.alert('Para fonte Banco de Dados, preencha host, banco, usuário e senha.');
      return;
    }

    try {
      setLoadingRequest(true);
      if (editingId) {
        await axios.put(`/api/apis/${editingId}`, payload);
      } else {
        await axios.post('/api/apis', payload);
      }

      await loadApis();
      resetForm();
      setRequestMessage(editingId ? 'API atualizada com sucesso.' : 'API cadastrada com sucesso.');
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Erro inesperado';
      setRequestMessage(`Erro ao salvar API: ${message}`);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleEdit = (api: ExternalApiDefinition) => {
    setForm({
      name: api.name,
      sourceType: api.sourceType || 'http',
      endpointUrl: api.endpointUrl,
      username: api.username,
      password: api.password,
      dbHost: api.dbHost || '',
      dbPort: api.dbPort || 3306,
      dbName: api.dbName || '',
      dbUser: api.dbUser || '',
      dbPassword: api.dbPassword || '',
      queryTemplate: api.queryTemplate,
      cacheInterval: api.cacheInterval || 0,
      createNow: api.createNow,
      published: api.published
    });
    setEditingId(api.id);
  };

  const handleDeactivate = (id: string) => {
    if (!window.confirm('Deseja realmente desativar esta API? Ela sera despublicada automaticamente.')) {
      return;
    }

    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return;

    setLoadingRequest(true);
    axios
      .patch(`/api/apis/${numericId}/deactivate`)
      .then(() => {
        setRequestMessage('API desativada com sucesso.');
        if (selectedApiId === id) {
          setSelectedApiId('');
        }
        if (editingId === numericId) {
          resetForm();
        }
        return loadApis();
      })
      .catch((error: any) => {
        const message = error?.response?.data?.error || error?.message || 'Erro inesperado';
        setRequestMessage(`Erro ao desativar API: ${message}`);
      })
      .finally(() => {
        setLoadingRequest(false);
      });
  };

  const handleRefreshCache = async (id: number) => {
    try {
      setLoadingRequest(true);
      await axios.post(`/api/apis/${id}/refresh-cache`);
      await loadApis();
      setRequestMessage('Cache atualizado com sucesso.');
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Erro inesperado';
      setRequestMessage(`Erro ao atualizar cache: ${message}`);
    } finally {
      setLoadingRequest(false);
    }
  };

  const togglePublished = async (id: number, published: boolean) => {
    try {
      setLoadingRequest(true);
      await axios.patch(`/api/apis/${id}/publish`, { published: !published });
      await loadApis();
      setRequestMessage(!published ? 'API publicada com sucesso.' : 'API despublicada com sucesso.');
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Erro inesperado';
      setRequestMessage(`Erro ao alterar publicacao: ${message}`);
    } finally {
      setLoadingRequest(false);
    }
  };

  const selectedApi = useMemo(() => {
    const parsedId = Number(selectedApiId);
    if (!Number.isFinite(parsedId)) return undefined;
    return apis.find(api => api.id === parsedId);
  }, [apis, selectedApiId]);

  useEffect(() => {
    if (!selectedApi) {
      setQueryInput('limit=10');
      return;
    }

    setQueryInput(
      selectedApi.sourceType === 'database'
        ? selectedApi.queryTemplate || 'SELECT * FROM sua_tabela LIMIT 20'
        : 'limit=10'
    );
  }, [selectedApiId, selectedApi]);

  const handleConsume = async () => {
    if (!selectedApi) {
      setRequestMessage('Selecione uma API para testar.');
      return;
    }

    if (!selectedApi.published) {
      setRequestMessage('Esta API ainda nao esta publicada para consumo externo.');
      return;
    }

    setLoadingRequest(true);
    setRequestMessage('');

    try {
      const response = await axios.post(`/api/apis/${selectedApi.id}/test`, {
        query: queryInput
      });

      const preview = JSON.stringify(response.data?.data ?? response.data, null, 2).slice(0, 300);
      setRequestMessage(`Consumo realizado com sucesso. Retorno inicial: ${preview}`);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Erro inesperado';
      setRequestMessage(`Erro ao consumir a API: ${message}`);
    } finally {
      setLoadingRequest(false);
    }
  };

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR');
  };

  const resolvePublicUrl = (path: string) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (typeof window === 'undefined') return normalizedPath;
    return `${window.location.origin}${normalizedPath}`;
  };

  const resolveSourceLabel = (sourceType: ExternalApiDefinition['sourceType']) => (
    sourceType === 'database' ? 'Banco de Dados' : 'API HTTP'
  );

  const resolveDatabaseLabel = (api: ExternalApiDefinition) => {
    if (api.sourceType !== 'database') return '-';
    return `${api.dbHost}:${api.dbPort}/${api.dbName}`;
  };

  return (
    <div className="apis-module">
      <header className="apis-header">
        <h2>APis Externas</h2>
        <p>
          Cadastre APIs para consumo externo, configure credenciais e publique quando estiver pronta.
          Para fonte Banco de Dados, informe host, banco, usuario e senha de conexao.
        </p>
      </header>

      <section className="apis-card">
        <h3>{editingId ? 'Editar API' : 'Nova API'}</h3>

        <form onSubmit={handleSubmit} className="apis-form">
          <div className="apis-grid">
            <label>
              Tipo de fonte
              <select name="sourceType" value={form.sourceType} onChange={handleInputChange}>
                <option value="http">API HTTP</option>
                <option value="database">Banco de Dados</option>
              </select>
              <small className="field-help">
                Escolha API HTTP para consumir URL externa ou Banco de Dados para executar SQL direto no banco configurado.
              </small>
            </label>

            <label>
              Nome da API
              <input
                name="name"
                value={form.name}
                onChange={handleInputChange}
                placeholder="Ex.: API de Status de Viagens"
                required
              />
            </label>

            {form.sourceType === 'http' && (
              <label>
                URL de consumo
                <input
                  name="endpointUrl"
                  value={form.endpointUrl}
                  onChange={handleInputChange}
                  placeholder="https://seu-dominio.com/api/externa"
                  required
                />
              </label>
            )}

            <label>
              Usuario de acesso externo
              <input
                name="username"
                value={form.username}
                onChange={handleInputChange}
                placeholder="usuario_integracao"
                required
              />
              <small className="field-help">
                Este usuario e para quem vai consumir sua API publicada, nao e o usuario do banco.
              </small>
            </label>

            <label>
              Senha de acesso externo
              <div className="password-row">
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder="********"
                  required
                />
                <button
                  type="button"
                  className="btn-inline"
                  onClick={() => setShowPassword(prev => !prev)}
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              <small className="field-help">
                Senha de autenticacao da API publicada para o consumidor externo.
              </small>
            </label>

            {form.sourceType === 'database' && (
              <>
                <div className="source-guide">
                  <strong>Como preencher Banco de Dados</strong>
                  <p>
                    Informe os dados de conexao do banco de origem. Use um usuario com permissao apenas de leitura.
                  </p>
                  <p>
                    Exemplo rapido: host 10.0.0.10, porta 3306, banco logistica, usuario integracao_leitura.
                  </p>
                </div>

                <label>
                  Host do banco
                  <input
                    name="dbHost"
                    value={form.dbHost}
                    onChange={handleInputChange}
                    placeholder="ex.: 10.0.0.10 ou banco.empresa.local"
                    required
                  />
                  <small className="field-help">IP ou DNS do servidor do banco.</small>
                </label>

                <label>
                  Porta
                  <input
                    name="dbPort"
                    type="number"
                    min={1}
                    max={65535}
                    step={1}
                    value={form.dbPort}
                    onChange={handleInputChange}
                    placeholder="3306"
                    required
                  />
                  <small className="field-help">MySQL geralmente usa 3306.</small>
                </label>

                <label>
                  Nome do banco
                  <input
                    name="dbName"
                    value={form.dbName}
                    onChange={handleInputChange}
                    placeholder="nome_do_banco"
                    required
                  />
                  <small className="field-help">Nome exato do schema/base a consultar.</small>
                </label>

                <label>
                  Usuario do banco
                  <input
                    name="dbUser"
                    value={form.dbUser}
                    onChange={handleInputChange}
                    placeholder="usuario_banco"
                    required
                  />
                  <small className="field-help">Usuario tecnico com acesso de leitura nas tabelas.</small>
                </label>

                <label>
                  Senha do banco
                  <input
                    name="dbPassword"
                    type="password"
                    value={form.dbPassword}
                    onChange={handleInputChange}
                    placeholder="********"
                    required
                  />
                  <small className="field-help">Senha do usuario do banco informado acima.</small>
                </label>
              </>
            )}
          </div>

          <label>
            {form.sourceType === 'database' ? 'Query SQL (SELECT)' : 'Template de query (opcional)'}
            <textarea
              name="queryTemplate"
              value={form.queryTemplate}
              onChange={handleInputChange}
              rows={3}
              placeholder={form.sourceType === 'database' ? 'SELECT * FROM sua_tabela LIMIT 100' : 'status=em_transito&data_inicio=2026-04-24'}
            />
          </label>

          <div className="switch-line">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="createNow"
                checked={form.createNow}
                onChange={handleCheckboxChange}
              />
              Criar API agora
            </label>

            <label className="checkbox-item">
              <input
                type="checkbox"
                name="published"
                checked={form.published}
                onChange={handleCheckboxChange}
              />
              Publicar para consumo
            </label>
          </div>

          <label>
            Intervalo de atualizacao automatica (cache)
            <select
              name="cacheInterval"
              value={String(form.cacheInterval)}
              onChange={handleInputChange}
            >
              <option value="0">Desativado (consulta ao vivo a cada acesso)</option>
              <option value="5">A cada 5 minutos</option>
              <option value="10">A cada 10 minutos</option>
              <option value="15">A cada 15 minutos</option>
              <option value="30">A cada 30 minutos</option>
              <option value="60">A cada 1 hora</option>
              <option value="120">A cada 2 horas</option>
              <option value="360">A cada 6 horas</option>
              <option value="720">A cada 12 horas</option>
            </select>
            <small className="field-help">
              Quando ativado, os dados sao buscados e salvos automaticamente no intervalo definido.
              O usuario final recebe os dados do cache — mais rapido e sem sobrecarregar o banco de origem.
            </small>
          </label>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editingId ? 'Salvar alteracoes' : 'Cadastrar API'}
            </button>
            {editingId && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancelar edicao
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="apis-card">
        <h3>APIs cadastradas</h3>
        {loadingList && <p className="request-feedback">Carregando APIs...</p>}
        {apis.length === 0 ? (
          <p className="empty-state">Nenhuma API cadastrada ainda.</p>
        ) : (
          <div className="apis-table-wrapper">
            <table className="apis-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Fonte</th>
                  <th>Banco</th>
                  <th>Ultimo Consumo</th>
                  <th>Cache</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {apis.map(api => (
                  <tr key={api.id}>
                    <td>
                      <div className="api-name-cell">
                        <strong>{api.name}</strong>
                        <span className={`status-pill ${api.published ? 'published' : 'draft'}`}>
                          {api.published ? 'Publicada' : 'Rascunho'}
                        </span>
                      </div>
                    </td>
                    <td>{resolveSourceLabel(api.sourceType)}</td>
                    <td>{resolveDatabaseLabel(api)}</td>
                    <td>{api.lastConsumedAt ? formatDateTime(api.lastConsumedAt) : 'Nunca consumida'}</td>
                    <td>
                      {api.cacheInterval > 0 ? (
                        <div className="cache-status-cell">
                          <span className={`status-pill cache-${api.cacheStatus}`}>
                            {api.cacheStatus === 'ok' ? 'Atualizado' : api.cacheStatus === 'erro' ? 'Erro' : 'Aguardando'}
                          </span>
                          <small>{api.cacheUpdatedAt ? formatDateTime(api.cacheUpdatedAt) : 'Nunca'}</small>
                          <small>A cada {api.cacheInterval} min</small>
                          {api.cacheStatus === 'erro' && api.cacheError && (
                            <small className="cache-error-msg" title={api.cacheError}>Erro: {api.cacheError.slice(0, 60)}</small>
                          )}
                        </div>
                      ) : (
                        <span className="cache-disabled">Desativado</span>
                      )}
                    </td>
                    <td>
                      <div className="api-item-actions">
                        <button type="button" className="btn-secondary" onClick={() => handleEdit(api)}>
                          Editar
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => togglePublished(api.id, api.published)}>
                          {api.published ? 'Despublicar' : 'Publicar'}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => handleDeactivate(String(api.id))}>
                          Desativar
                        </button>
                        {api.cacheInterval > 0 && (
                          <button type="button" className="btn-inline" onClick={() => handleRefreshCache(api.id)} disabled={loadingRequest}>
                            Atualizar Cache
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="apis-card">
        <h3>Teste de consumo</h3>
        <p>Simule como o usuario final vai consumir a API publicada.</p>

        <div className="consume-grid">
          <label>
            API publicada
            <select
              value={selectedApiId}
              onChange={(e) => setSelectedApiId(e.target.value)}
            >
              <option value="">Selecione uma API</option>
              {apis.map(api => (
                <option key={api.id} value={api.id}>
                  {api.name} {api.published ? '' : '(nao publicada)'}
                </option>
              ))}
            </select>
          </label>

          <label>
            Query para teste
            <input
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder={selectedApi?.sourceType === 'database' ? 'SELECT * FROM sua_tabela LIMIT 20' : 'status=ativo&pagina=1'}
            />
            <small className="field-help">
              {selectedApi?.sourceType === 'database'
                ? 'Para Banco de Dados, informe SQL de leitura para teste (SELECT/WITH).'
                : 'Para API HTTP, informe query string de teste (ex.: status=ativo&pagina=1).'}
            </small>
          </label>
        </div>

        {selectedApi && (
          <p>
            URL para consumo externo: <strong>{resolvePublicUrl(selectedApi.publicPath)}</strong>
          </p>
        )}

        <button type="button" className="btn-primary" onClick={handleConsume} disabled={loadingRequest}>
          {loadingRequest ? 'Consumindo...' : 'Testar consumo'}
        </button>

        {requestMessage && <p className="request-feedback">{requestMessage}</p>}
      </section>
    </div>
  );
};

export default ApisModule;
